import type { ShopifyProduct, ShopifyProductVariant } from "@/types/shopify";
import { parseStringProduct } from "@/lib/catalog/shopify-catalog";
import { canPurchase } from "@/lib/products/availability";
import { PRODUCT_TYPE_CATEGORY } from "@/lib/shopify/sync-config";
import type { StringProductMeta } from "@/types/catalog";

// ─────────────────────────────────────────────────────────
// Purchase eligibility — one decision, used everywhere.
//
// Product cards, the product page, /api/cart/add and the product structured
// data each decided independently whether an item could be bought. Structured
// data was the odd one out: it published every variant as an InStock Offer even
// for records the storefront always refuses, so search engines advertised a
// price and availability for something the site will not sell.
//
// A specification block is NOT an inventory state. It is not "sold out" and it
// is certainly not "coming soon" — the record contradicts itself and no one
// knows which set it describes until an operator resolves it.
// ─────────────────────────────────────────────────────────

export type PurchaseBlock =
  /** The Shopify record's handle and title describe different sets. */
  | "specification-under-review";

/** Why this product cannot be sold at all, independent of stock. null = no block. */
export function purchaseBlock(meta: Pick<StringProductMeta, "gaugeConflict">): PurchaseBlock | null {
  return meta.gaugeConflict ? "specification-under-review" : null;
}

/** Convenience for call sites that only need the boolean. */
export function isPurchaseBlocked(meta: Pick<StringProductMeta, "gaugeConflict">): boolean {
  return purchaseBlock(meta) !== null;
}

/**
 * Cart-boundary decision taken straight from a Shopify product record. A
 * client can submit any syntactically valid ProductVariant GID, so require a
 * recognized string product and a complete, non-conflicting specification —
 * not merely a product whose parser happened not to find a conflict.
 */
export function productPurchaseBlocked(
  product: Pick<ShopifyProduct, "handle" | "title" | "productType"> &
    Partial<Pick<ShopifyProduct, "metafields">>,
): boolean {
  try {
    const meta = parseStringProduct({
      handle: product.handle,
      title: product.title,
      productType: product.productType,
      metafields: product.metafields ?? [],
    });
    const knownProductType = Object.hasOwn(PRODUCT_TYPE_CATEGORY, product.productType.trim());
    const completeGaugeList = meta.stringCount > 0 && meta.individualGauges.trim().length > 0 &&
      meta.individualGauges.split("|").filter((value) => value.trim().length > 0).length === meta.stringCount;
    return !knownProductType || !["aurora-one", "ultra-sheer", "ultra-flex", "prime", "stainless-steel"].includes(meta.productLineSlug) ||
      !["electric", "acoustic", "bass", "classical", "ukulele"].includes(meta.category) ||
      !completeGaugeList || isPurchaseBlocked(meta);
  } catch {
    return true;
  }
}

/** True only when this exact variant is both unblocked and in stock. */
export function variantPurchasable(
  meta: Pick<StringProductMeta, "gaugeConflict">,
  variant: ShopifyProductVariant | null | undefined,
): boolean {
  return !isPurchaseBlocked(meta) && canPurchase(variant);
}
